import * as THREE from 'three';
import createAnimation from '../converter';

import type { AnimationClip, AnimationMixer } from 'three';

/**
 * Animation Priority System
 * Higher priority animations can interrupt lower priority ones
 * Lower numbers = higher priority
 */
export enum AnimationPriority {
  SPEAKING = 1,    // Highest priority - viseme/lip-sync animations
  THINKING = 2,    // Head/eye movements during thinking
  LISTENING = 3,   // Head/eye movements during listening
  IDLE = 4         // Lowest priority - idle animations
}

/**
 * Animation types for state management
 */
export enum AnimationType {
  SPEAKING = 'speaking',
  THINKING = 'thinking',
  LISTENING = 'listening',
  IDLE = 'idle'
}

/**
 * Check if a new animation can interrupt the current animation
 * @param currentPriority Current animation priority
 * @param newPriority New animation priority
 * @returns true if new animation can interrupt current
 */
export function canInterrupt(currentPriority: AnimationPriority | null, newPriority: AnimationPriority): boolean {
  if (currentPriority === null) return true;
  return newPriority < currentPriority; // Lower number = higher priority
}

/**
 * Get priority for animation type
 */
export function getPriorityForType(type: AnimationType): AnimationPriority {
  switch (type) {
    case AnimationType.SPEAKING:
      return AnimationPriority.SPEAKING;
    case AnimationType.THINKING:
      return AnimationPriority.THINKING;
    case AnimationType.LISTENING:
      return AnimationPriority.LISTENING;
    case AnimationType.IDLE:
      return AnimationPriority.IDLE;
    default:
      return AnimationPriority.IDLE;
  }
}

export function animateAvatar(blendData: any, mixer: AnimationMixer | null, morphTargetDictionaryBody: Record<string, number> | null, morphTargetDictionaryLowerTeeth: Record<string, number> | null, bodyName: string = 'HG_Body', lowerTeethName: string = 'HG_TeethLower') {
  try {
    if (!mixer || !morphTargetDictionaryBody || !morphTargetDictionaryLowerTeeth) {
      console.error('Missing required components for animation');
      return;
    }

    const clipBody = createAnimation(blendData, morphTargetDictionaryBody, bodyName) as AnimationClip;
    const actionBody = mixer.clipAction(clipBody);
    actionBody.setLoop(THREE.LoopOnce, 1);
    actionBody.clampWhenFinished = true;
    actionBody.play();

    const clipLowerTeeth = createAnimation(blendData, morphTargetDictionaryLowerTeeth, lowerTeethName) as AnimationClip;
    const actionLowerTeeth = mixer.clipAction(clipLowerTeeth);
    actionLowerTeeth.setLoop(THREE.LoopOnce, 1);
    actionLowerTeeth.clampWhenFinished = true;
    actionLowerTeeth.play();

    console.log('Animation started');
  } catch (error) {
    console.error('Error animating avatar:', error);
  }
}

export function stopAllAnimations(mixer: AnimationMixer | null) {
  try {
    if (mixer) {
      mixer.stopAllAction();
      console.log('All animations stopped');
    }
  } catch (error) {
    console.error('Error stopping animations:', error);
  }
}
